/**
 * Msg 560 · D.2 — Wiki-tree Topics UI (page components).
 *
 * Sub-components + shared constants live in `AtlasTopicParts.jsx`.
 * Kept here: the three page components exported to App.js.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, ThumbsUp, MessageSquareQuote, Sparkles,
  Pencil, Trash2, Plus, ChevronRight, Folder, Paperclip,
  ArrowUpRight, Home, AlertTriangle, X as CloseIcon, Compass,
  Move as MoveIcon, CheckSquare, Square,
  Layers, Wrench, Link2, Settings, Palette, ArrowRightLeft, Upload,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
// 2026-08-10 · LiveAccommodationsStrip unmounted from the folder
// detail view per dispatch spec. The component file itself is
// retained — AtlasSmartCompareFiltered.jsx still uses it.
// import LiveAccommodationsStrip from "@/components/atlas/LiveAccommodationsStrip";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";

import {
  humanize,
  Chip, CountPill, EntityIcon,
  TopicFormModal, DeleteStrategyModal,
} from "./AtlasTopicShared";
import { useAtlasCategories } from "./atlasCategoriesCache";
import { AttachmentsStrip } from "./AtlasTopicAttachments";
import { LeftRailTree, StructuredAttributesPanel } from "./AtlasTopicTree";
import { TopicSearchBar } from "./AtlasTopicSearchBar";
import { MoveTopicModal } from "./AtlasTopicMove";
import { MergeDuplicatesModal, DuplicateAlertPill } from "./AtlasTopicMerge";
import FactSourceInfoBadge from "./FactSourceInfoBadge";
import UploadDocumentModal from "./UploadDocumentModal";
import VerificationBadge from "./AtlasVerificationBadge";
import TextareaAutosize from "react-textarea-autosize";
// Msg 575 — embed admin taxonomy tools inside the Topics tab.
import {
  AtlasTemplatesListPage,
  AtlasTemplateEditorPage,
  AtlasTemplateParentMapPage,
} from "./admin/AtlasTemplatesAdmin";
import AtlasBulkAttributesPage from "./admin/AtlasBulkAttributes";


// ─────────────────────────────────────────────────────────────
// Landing — grid of root topics.
// ─────────────────────────────────────────────────────────────
export function AtlasTopicsBrowsePage() {
  const nav = useNavigate();
  const [rootRows, setRootRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  const [showUploadDash, setShowUploadDash] = useState(false);
  // Msg 575 · Topics-tab consolidation — segmented control controls
  // which admin surface renders in place of the Categories grid.
  const [sp, setSp] = useSearchParams();
  const view = sp.get("view") || "categories";
  const { user } = useAuth();
  const roles = user?.roles || (user?.role ? [user.role] : []);
  const isAdmin = roles.includes("atlas_admin");
  const setView = (v) => {
    const next = new URLSearchParams(sp);
    if (!v || v === "categories") next.delete("view");
    else next.set("view", v);
    next.delete("open");
    setSp(next);
  };

  const load = useCallback(() => {
    setLoading(true);
    api.get("/atlas/topics/tree?depth=1")
      .then((r) => {
        // Msg 584 · P0 — do NOT overwrite `child_count`. The backend
        // computes it from a real children lookup; the old client
        // fallback `(row.children || []).length` was always 0 because
        // /tree?depth=1 returns empty `children` arrays for roots
        // (level=2 > depth=1). Preserve backend-supplied
        // `descendant_count` for the "topics total" pill.
        const rows = (r.data.tree || []).map((row) => ({
          ...row,
          child_count: (typeof row.child_count === "number")
            ? row.child_count
            : (row.children || []).length,
          descendant_count: (typeof row.descendant_count === "number")
            ? row.descendant_count
            : 0,
        }));
        // Msg 586 · Part B — respect `sort_priority` in the roots
        // grid too (higher priority pinned first, then alphabetical
        // fallback). Backend already sorts this way, but the map()
        // above and the localeCompare here would otherwise re-order
        // by name only and lose pins.
        rows.sort((a, b) => {
          const pa = (typeof a.sort_priority === "number") ? a.sort_priority : 0;
          const pb = (typeof b.sort_priority === "number") ? b.sort_priority : 0;
          if (pa !== pb) return pb - pa;
          return (a.name || "").localeCompare(b.name || "");
        });
        setRootRows(rows);
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { if (view === "categories") load(); }, [load, view]);

  const openNewCategory = () => setShowNudge(true);
  const confirmNewCategory = () => { setShowNudge(false); setShowNew(true); };
  const pickParentInstead = () => {
    setShowNudge(false);
    try {
      const el = document.querySelector(
        '[data-testid="atlas-topics-landing-search"] input'
      );
      if (el) el.focus();
    } catch { /* noop */ }
  };

  // Render the embedded admin surface for the currently-selected view.
  // Non-admins never reach these branches — the toolbar simply isn't shown.
  const renderView = () => {
    if (view === "templates") {
      // ?open=<key> → editor; otherwise the grid.
      return sp.get("open")
        ? <AtlasTemplateEditorPage />
        : <AtlasTemplatesListPage />;
    }
    if (view === "parent-map") return <AtlasTemplateParentMapPage />;
    if (view === "bulk") return <AtlasBulkAttributesPage />;
    return null;
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl" data-testid="atlas-topics-landing">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-tb-navy text-3xl md:text-4xl font-semibold"
              style={{ fontFamily: '"Playfair Display", serif' }}>
            TravelBio Topics
          </h1>
          <p className="mt-1 font-hand text-tb-navy/60 text-sm max-w-xl">
            A living wiki-tree of destinations, ships, resorts, ports, and the specifics your team knows best.
            Every node holds structured attributes, attachments, and advisor tips.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Msg 582 · L.3 — gear menu replacing the 4-chip admin toolbar.
              Only rendered for atlas_admin. Advisor-facing view stays
              clean; taxonomy tools open in a right-side Sheet so the
              categories grid stays visible behind them. */}
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button"
                  data-testid="atlas-topics-admin-gear"
                  aria-label="Admin tools"
                  title="Admin tools"
                  className="inline-flex items-center gap-1.5 h-11 px-3 rounded-full border border-tb-navy/15 bg-white hover:bg-tb-navy/5 text-tb-navy font-hand text-xs">
                  <Settings className="w-4 h-4" />
                  <span className="hidden sm:inline">Admin tools</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72"
                data-testid="atlas-topics-admin-menu">
                <DropdownMenuLabel>Taxonomy admin</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="atlas-topics-admin-menu-presets"
                  onSelect={() => nav("/atlas/admin/presets")}
                  className="flex-col items-start gap-0.5">
                  <span className="inline-flex items-center gap-1.5 font-medium">
                    <Sparkles className="w-3.5 h-3.5" /> Folder Presets
                  </span>
                  <span className="text-[11px] text-tb-navy/60">
                    Pick target folders and apply sub-folders + attributes in one pass.
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="atlas-topics-admin-menu-categories"
                  onSelect={() => nav("/atlas/admin/categories")}
                  className="flex-col items-start gap-0.5">
                  <span className="inline-flex items-center gap-1.5 font-medium">
                    <Palette className="w-3.5 h-3.5" /> Categories
                  </span>
                  <span className="text-[11px] text-tb-navy/60">
                    Define the categories advisors pick when tagging a folder — controls left-rail icons.
                  </span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <button type="button"
            onClick={openNewCategory}
            data-testid="atlas-topics-new-category"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm">
            <Plus className="w-4 h-4" /> New category
          </button>
          {isAdmin && (
            <button type="button"
              onClick={() => setShowUploadDash(true)}
              data-testid="atlas-topics-upload-document"
              title="Upload a document and extract facts"
              className="inline-flex items-center gap-2 h-11 px-4 rounded-full border border-tb-navy/25 bg-white text-tb-navy hover:bg-tb-navy/5 font-hand text-sm">
              <Upload className="w-4 h-4" /> Upload document
            </button>
          )}
        </div>
      </div>
      <UploadDocumentModal
        open={showUploadDash}
        onClose={() => setShowUploadDash(false)} />

      {/* Categories grid — always rendered. Admin tools now open in a
          Sheet (below) instead of swapping this content out. */}
      <div className="mt-6 max-w-2xl">
        <TopicSearchBar
          size="lg"
          testId="atlas-topics-landing-search"
          placeholder="Search topics — e.g. Wonder of the Seas, Aruba, resorts…"
        />
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
           data-testid="atlas-topics-root-grid">
        {loading && Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-32 rounded-2xl bg-tb-navy/5 animate-pulse" />
        ))}
        {!loading && rootRows.length === 0 && (
          <div className="col-span-full text-center py-14 rounded-3xl border border-dashed border-tb-navy/20 bg-tb-navy/[0.02]"
               data-testid="atlas-topics-empty">
            <div className="mx-auto h-12 w-12 rounded-full bg-tb-navy/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-tb-navy/60" />
            </div>
            <p className="mt-3 font-hand text-tb-navy/70 text-sm">
              No categories yet. Create your first one to start the tree.
            </p>
            <button type="button"
              onClick={openNewCategory}
              data-testid="atlas-topics-empty-new"
              className="mt-4 inline-flex items-center gap-2 h-10 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm">
              <Plus className="w-4 h-4" /> New category
            </button>
          </div>
        )}
        {!loading && rootRows.map((t) => (
          <RootTopicCard key={t.slug} t={t} onOpen={() => nav(`/atlas/topics/${t.slug}`)} />
        ))}
      </div>

      <EmptyScaffoldsRow onOpen={(slug) => nav(`/atlas/topics/${slug}`)} />

      {/* Msg 582 · L.3 — admin taxonomy tools render in a right-side
          sheet. Legacy `?view=` deep-links still open the correct
          tool. Sheet close resets `?view=categories`. */}
      {isAdmin && (
        <Sheet
          open={view !== "categories"}
          onOpenChange={(o) => { if (!o) setView("categories"); }}
        >
          <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto"
            data-testid={`atlas-topics-admin-sheet-${view}`}>
            <SheetHeader>
              <SheetTitle>
                {view === "templates" && "Templates"}
                {view === "parent-map" && "Parent map"}
                {view === "bulk" && "Bulk attributes"}
              </SheetTitle>
              <SheetDescription>
                {view === "templates" && "Set the default folders and fields that get created when adding a new topic under a category."}
                {view === "parent-map" && "Choose which template auto-applies based on where a topic is created."}
                {view === "bulk" && "Search and edit attributes across many topics at once."}
              </SheetDescription>
            </SheetHeader>
            <div className="mt-4">
              {renderView()}
            </div>
          </SheetContent>
        </Sheet>
      )}

      <NewCategoryNudgeDialog
        open={showNudge}
        onCancel={() => setShowNudge(false)}
        onConfirm={confirmNewCategory}
        onPickParent={pickParentInstead}
      />

      <TopicFormModal
        mode="create"
        open={showNew}
        onClose={() => setShowNew(false)}
        parentSlug=""
        onSaved={(t) => {
          setShowNew(false);
          if (t?.slug) nav(`/atlas/topics/${t.slug}`);
          else load();
        }}
      />
    </div>
  );
}


// Msg 575 · segmented toolbar chip.
// K2-relatedlookup (2026-08-05) — reverse-lookup section on the topic
// focus page. Renders every topic that has a relationship-fact pointing
// AT this topic, grouped by relationship type ("Ships that visit
// [this] as a port of call", "Ships whose homeport is [this]", …).
// Returns null when the reverse lookup found nothing.
// K2-discover (2026-08-05) — 2-hop discovery widget. Renders below
// RelatedSection. Hidden when the endpoint returns no items.
function DiscoverSection({ discover, sourceName }) {
  if (!discover || (discover.count || 0) === 0) return null;
  return (
    <section className="mt-8" data-testid="atlas-topic-discover">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-tb-navy text-lg font-semibold"
              style={{ fontFamily: '"Playfair Display", serif' }}>
            You might also brief your clients on…
          </h2>
          <p className="mt-0.5 font-hand text-tb-navy/50 text-[11px]">
            Topics that share ports, regions, or relationships with{" "}
            <b>{sourceName}</b>.
          </p>
        </div>
        <span className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/40"
              data-testid="atlas-topic-discover-count">
          {discover.count} suggestion{discover.count === 1 ? "" : "s"}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {discover.items.map((it) => (
          <Link key={it.slug}
            to={`/atlas/topics/${it.slug}`}
            data-testid={`atlas-topic-discover-item-${it.slug}`}
            className="rounded-xl border border-tb-navy/15 bg-white hover:border-tb-navy/35 hover:bg-tb-navy/[0.02] transition-colors p-3">
            <div className="flex items-baseline gap-2">
              <span className="text-tb-navy font-semibold text-sm truncate"
                    style={{ fontFamily: '"Playfair Display", serif' }}>
                {it.name}
              </span>
              <span className="ml-auto text-[10px] uppercase tracking-widest font-hand px-1.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 flex-shrink-0"
                    data-testid={`atlas-topic-discover-shared-${it.slug}`}>
                shares {it.shared_count}
              </span>
            </div>
            <div className="mt-0.5 text-tb-navy/45 font-hand text-[11px] truncate">
              {it.breadcrumb}
            </div>
            {it.shared_targets && it.shared_targets.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {it.shared_targets.slice(0, 4).map((t) => (
                  <span key={`${it.slug}-${t.slug}`}
                    className="text-[10px] font-hand text-tb-navy/60 px-1.5 py-0.5 rounded-full border border-tb-navy/15">
                    {t.name}
                  </span>
                ))}
                {it.shared_targets.length > 4 && (
                  <span className="text-[10px] font-hand text-tb-navy/40">
                    +{it.shared_targets.length - 4}
                  </span>
                )}
              </div>
            )}
          </Link>
        ))}
      </div>
    </section>
  );
}



function RelatedSection({ related }) {
  if (!related || (related.count || 0) === 0) return null;
  const humanize = (rel) => {
    const map = {
      port_of_call:  "Visit this as a port of call",
      homeport:      "Homeport here",
      region_sailed: "Sail this region",
      cabin_class:   "Have this cabin class",
      operated_by:   "Operated by this",
      sister_ship:   "Sister ships of this",
      flag_state:    "Fly this flag",
      cruise_line:   "Belong to this cruise line",
    };
    return map[rel] || (rel || "related").replace(/_/g, " ");
  };
  return (
    <section className="mt-8" data-testid="atlas-topic-related">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-tb-navy text-lg font-semibold"
              style={{ fontFamily: '"Playfair Display", serif' }}>
            Related topics
          </h2>
          <p className="mt-0.5 font-hand text-tb-navy/50 text-[11px]">
            Other places in the atlas that link here through an ingested relationship.
          </p>
        </div>
        <span className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/40"
              data-testid="atlas-topic-related-count">
          {related.count} link{related.count === 1 ? "" : "s"}
        </span>
      </div>

      <div className="mt-3 space-y-4">
        {related.groups.map((g) => (
          <div key={g.relationship}
               data-testid={`atlas-topic-related-group-${g.relationship}`}
               className="rounded-xl border border-tb-navy/15 bg-white p-3">
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-[10px] uppercase tracking-widest font-hand px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800">
                {g.relationship.replace(/_/g, " ")}
              </span>
              <span className="text-tb-navy font-hand text-sm">
                {humanize(g.relationship)}
              </span>
              <span className="ml-auto text-[10px] font-hand text-tb-navy/40">
                {g.count}
              </span>
            </div>
            <ul className="space-y-1">
              {g.items.map((it) => (
                <li key={`${g.relationship}-${it.slug}`}>
                  <Link to={`/atlas/topics/${it.slug}`}
                    data-testid={`atlas-topic-related-item-${g.relationship}-${it.slug}`}
                    className="flex items-baseline gap-2 rounded-lg px-2 py-1 hover:bg-tb-navy/5">
                    <span className="text-tb-navy font-hand text-sm font-semibold">
                      {it.name}
                    </span>
                    <span className="text-tb-navy/40 font-hand text-[11px] truncate">
                      {it.breadcrumb}
                    </span>
                    {it.source_facts_count > 1 && (
                      <span className="ml-auto text-[10px] font-hand text-tb-navy/40">
                        ×{it.source_facts_count}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}



function ToolbarChip({ active, onClick, children, icon, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      data-active={active ? "1" : "0"}
      className={
        "inline-flex items-center gap-1.5 h-9 px-4 rounded-full font-hand text-xs transition-colors " +
        (active
          ? "bg-tb-navy text-tb-gold-bright"
          : "text-tb-navy/70 hover:bg-tb-navy/5")
      }>
      {icon}{children}
    </button>
  );
}


function EmptyScaffoldsRow({ onOpen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api.get("/atlas/topics/empty-scaffolds?limit=5")
      .then((r) => { if (!cancelled) setRows(r.data?.results || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
  if (loading || rows.length === 0) return null;
  return (
    <section className="mt-10 rounded-3xl border border-tb-navy/10 bg-tb-navy/[0.02] p-5"
             data-testid="atlas-topics-empty-scaffolds-row">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-tb-navy text-lg font-semibold flex items-center gap-2"
              style={{ fontFamily: '"Playfair Display", serif' }}>
            <Sparkles className="w-4 h-4 text-tb-gold-bright" />
            Empty scaffolds needing content
          </h2>
          <p className="mt-0.5 font-hand text-[11px] text-tb-navy/60">
            Template folders sitting empty — ordered by how active their parent is.
            A quick add here compounds fast.
          </p>
        </div>
      </div>
      <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
        {rows.map((r) => (
          <li key={r.slug}>
            <button type="button" onClick={() => onOpen?.(r.slug)}
              data-testid={`empty-scaffold-row-${r.slug}`}
              className="w-full text-left rounded-xl border border-tb-navy/15 bg-white hover:border-tb-navy transition p-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-tb-navy text-sm font-semibold truncate"
                     style={{ fontFamily: '"Playfair Display", serif' }}>
                  {r.parent_name} → {r.name}
                </div>
                <div className="mt-0.5 font-hand text-[10px] uppercase tracking-widest text-tb-navy/50">
                  {r.parent_child_count} sibling{r.parent_child_count === 1 ? "" : "s"} nearby
                </div>
              </div>
              <ArrowUpRight className="w-4 h-4 text-tb-navy/40 shrink-0" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}


function NewCategoryNudgeDialog({ open, onCancel, onConfirm, onPickParent }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-4"
         data-testid="atlas-topics-new-category-nudge"
         onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl border border-tb-navy/15">
        <div className="px-6 pt-5 pb-3 border-b border-tb-navy/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h2 className="text-tb-navy text-xl font-semibold"
                style={{ fontFamily: '"Playfair Display", serif' }}>
              Create a new category?
            </h2>
          </div>
          <button type="button" onClick={onCancel}
            data-testid="atlas-topics-nudge-close"
            className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-tb-navy/5 text-tb-navy/60">
            <CloseIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-3">
          <p className="font-hand text-tb-navy/80 text-sm">
            Most new topics belong under an existing category like
            <b> Countries</b>, <b>Cruise Lines</b>, <b>Resort Brands</b>,
            <b> Traveler Profiles</b>, or <b>Travel Styles</b>.
          </p>
          <p className="font-hand text-tb-navy/60 text-xs">
            You're about to create a <b>top-level category</b> at the root of the tree.
            Only add one when nothing existing fits.
          </p>
        </div>
        <div className="px-6 pb-5 pt-1 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2">
          <button type="button" onClick={onPickParent}
            data-testid="atlas-topics-nudge-pick-parent"
            className="inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-sm hover:bg-tb-navy/5">
            <Compass className="w-4 h-4" /> Pick a parent instead
          </button>
          <button type="button" onClick={onConfirm}
            data-testid="atlas-topics-nudge-confirm"
            className="inline-flex items-center justify-center gap-1.5 h-10 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm">
            <Plus className="w-4 h-4" /> Yes, create category
          </button>
        </div>
      </div>
    </div>
  );
}


function RootTopicCard({ t, onOpen }) {
  return (
    <button type="button" onClick={onOpen}
      data-testid={`atlas-topic-root-card-${t.slug}`}
      className="text-left rounded-2xl border border-tb-navy/15 hover:border-tb-navy hover:shadow-sm transition p-5 bg-white">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-tb-navy text-xl leading-tight truncate"
              style={{ fontFamily: '"Playfair Display", serif' }}>{t.name}</h2>
          <div className="mt-2 flex items-center gap-1.5 flex-wrap">
            <Chip testId={`root-card-cat-${t.slug}`}>{humanize(t.category)}</Chip>
            {t.entity_type && t.entity_type !== "other" && (
              <Chip tone="gold" testId={`root-card-et-${t.slug}`}>
                <EntityIcon categoryId={t.category_id} className="w-3 h-3" />
                {humanize(t.entity_type)}
              </Chip>
            )}
          </div>
        </div>
        <ArrowUpRight className="w-4 h-4 text-tb-navy/40 shrink-0" />
      </div>
      <div className="mt-4 flex items-center gap-4">
        {/* Msg 584 · P0 — landing card pills now read the fields the
            /topics/tree endpoint actually populates:
              `child_count`     → direct sub-topics
              `descendant_count`→ total topics under this root
            Previously the second pill read `narrative_fact_count`
            (never populated on tree rows) so every root showed "0". */}
        <CountPill icon={Folder} count={t.child_count || 0}
          label={(t.child_count || 0) === 1 ? "sub-topic" : "sub-topics"}
          testId={`root-card-children-${t.slug}`} />
        <CountPill icon={Layers} count={t.descendant_count || 0}
          label={(t.descendant_count || 0) === 1 ? "topic total" : "topics total"}
          testId={`root-card-descendants-${t.slug}`} />
      </div>
      {t.description && (
        <p className="mt-3 font-hand text-tb-navy/70 text-xs line-clamp-2">{t.description}</p>
      )}
    </button>
  );
}


// ─────────────────────────────────────────────────────────────
// Focus view.
// ─────────────────────────────────────────────────────────────
export function AtlasTopicDetailPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  // Msg 575 · admin-only "Edit template →" affordance in the header.
  const { user } = useAuth();
  const roles = user?.roles || (user?.role ? [user.role] : []);
  const isAdmin = roles.includes("atlas_admin");

  const [topic, setTopic] = useState(null);
  const [ancestors, setAncestors] = useState([]);
  const [children, setChildren] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [advisorTipFacts, setAdvisorTipFacts] = useState([]);
  const [travelerReviews, setTravelerReviews] = useState([]);
  const [narrativeFacts, setNarrativeFacts] = useState([]);
  const [activePromos, setActivePromos] = useState([]);
  const [activeNotices, setActiveNotices] = useState([]);
  const [myPending, setMyPending] = useState([]);
  // Phase 4 (Batch 2) — live Stay22 accommodations strip. Null when
  // the folder isn't place-based (e.g. cruise ship) or has no
  // resolvable geo. Shape: {offers[], provider, cache_hit, ...} or
  // {rate_limited: true, retry_after_ms: N}.
  const [liveAccommodations, setLiveAccommodations] = useState(null);
  // K2-relatedlookup (2026-08-05) — reverse lookup by relationship.
  const [related, setRelated] = useState({ count: 0, groups: [] });
  // K2-discover (2026-08-05) — 2-hop discovery widget.
  const [discover, setDiscover] = useState({ count: 0, items: [] });
  const [childCount, setChildCount] = useState(0);
  const [narrativeCount, setNarrativeCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAllNarrative, setShowAllNarrative] = useState(false);
  // Verification badge state — hydrated on initial topic load and
  // refreshed via 15 s poll on the lightweight verification endpoint.
  const [verifyState, setVerifyState] = useState(null);

  const [showEdit, setShowEdit] = useState(false);
  const [editFocusField, setEditFocusField] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [showChild, setShowChild] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedChildren, setSelectedChildren] = useState(() => new Set());
  const [showBulkMove, setShowBulkMove] = useState(false);
  const [allTopics, setAllTopics] = useState([]);
  const [treeNonce, setTreeNonce] = useState(0);
  // Msg 601 · P0 fix — inline modal for "Add narrative fact". The
  // previous handler navigated to /atlas/knowledge (retired route
  // in App.js — redirects to /atlas/topics) so the user was bounced
  // home with no editor. This modal submits directly to the current
  // slug via POST /api/atlas/facts, matching the flow the retired
  // Knowledge Builder used to run.
  const [showAddNarrative, setShowAddNarrative] = useState(false);

  // Msg 597 · P0 fix — split "initial load" (toggles `loading` and
  // wipes topic on error → shows "Topic not found") from "silent
  // refresh" (used after mutations; NEVER wipes topic on transient
  // errors and NEVER flips the Loading… state). Previously the same
  // helper served both roles, which meant any transient blip after a
  // save flashed the empty state at the user.
  const fetchTopic = useCallback(({ silent = false } = {}) => {
    if (!slug) return;
    if (!silent) setLoading(true);
    Promise.all([
      api.get(`/atlas/topics/${slug}`),
      api.get(`/atlas/topics/${slug}/ancestors`),
      api.get(`/atlas/topics/${slug}/children`),
      api.get(`/atlas/topics/${slug}/related`).catch(() => ({ data: { count: 0, groups: [] } })),
      api.get(`/atlas/topics/${slug}/discover?limit=6`).catch(() => ({ data: { count: 0, items: [] } })),
    ]).then(([tR, aR, cR, rR, dR]) => {
      setTopic(tR.data.topic);
      setVerifyState(tR.data.verification || null);
      setAttachments(tR.data.topic?.attachments || []);
      setChildCount(tR.data.child_count || 0);
      setNarrativeCount(tR.data.narrative_fact_count || 0);
      setAdvisorTipFacts(tR.data.advisor_tip_facts || []);
      setTravelerReviews(tR.data.traveler_reviews || []);
      // (2026-08-04) Bug 1 fix — narrative panel must NOT include
      // rows that already surface elsewhere. Previously the filter
      // only excluded attribute-key-bearing rows, so any fact stored
      // with `display_type: "advisor_tip"` or `"traveler_review"`
      // rendered twice. Backend now also groups `display_type:
      // "narrative"` into its own pool; we prefer that pool and fall
      // back to the legacy filter for pre-existing rows that were
      // silently stored as "attribute" without an `attribute_key`.
      const explicitNarrative = tR.data.narrative_facts || [];
      const legacyNarrative = (tR.data.canonical_facts || []).filter(
        (f) => !f.attribute_key
          && f.display_type !== "advisor_tip"
          && f.display_type !== "traveler_review"
          && f.display_type !== "narrative"
      );
      const seen = new Set(explicitNarrative.map((f) => f.id));
      const narr = [...explicitNarrative, ...legacyNarrative.filter((f) => !seen.has(f.id))];
      narr.sort((a, b) => (b.updated_at || b.created_at || "").localeCompare(a.updated_at || a.created_at || ""));
      setNarrativeFacts(narr);
      setActivePromos(tR.data.active_promos || []);
      setActiveNotices(tR.data.active_notices || []);
      setMyPending(tR.data.my_pending || []);
      setLiveAccommodations(tR.data.live_accommodations || null);
      setAncestors(aR.data.ancestors || []);
      setChildren(cR.data.children || []);
      setRelated({ count: rR.data?.count || 0, groups: rR.data?.groups || [] });
      setDiscover({ count: dR.data?.count || 0, items: dR.data?.items || [] });
    }).catch((err) => {
      // Only the INITIAL (non-silent) fetch may wipe topic → show the
      // "Topic not found" screen. A silent refresh keeps existing
      // topic/ancestors/children state intact so a transient network
      // glitch (or a same-slug 404 during a rename race) does not
      // vanish the page under the user's cursor.
      if (silent) {
        // Refresh may be sent when the slug briefly 404s (rename/merge
        // races). Do nothing visible. Console-log for triage.
        // eslint-disable-next-line no-console
        console.debug("[atlas.focus] silent refresh failed", err?.response?.status);
        return;
      }
      setTopic(null);
    }).finally(() => {
      if (!silent) setLoading(false);
    });
  }, [slug]);
  const load = fetchTopic; // legacy name kept for the initial useEffect
  useEffect(() => { fetchTopic({ silent: false }); }, [fetchTopic]);

  // Msg 586 · General fix — single helper for invalidating the left
  // rail tree cache. Called from EVERY topic mutation success path
  // (create, rename, edit, move, move-to-top, merge, delete) so
  // future fields don't regress into the "sidebar keeps old value"
  // class of bugs the user just hit renaming Ships → Fleet.
  //
  // Msg 596 · Bug fix — also refetch the current focus topic so
  // header state (name, category chip, template pill) refreshes when
  // a mutation happens FROM the left-rail kebab (e.g. Edit-topic
  // opened via the tree menu). Previously only the tree nonce was
  // bumped; the focus header kept the pre-save chip label.
  //
  // Msg 597 · P0 fix — refresh silently (see `fetchTopic`) so a
  // mutation on an UNRELATED topic (e.g. renaming a sibling in the
  // rail) never turns the current focus page into a "Topic not
  // found" flash while the refetch is in flight.
  const invalidateAtlasTree = useCallback(() => {
    setTreeNonce((n) => n + 1);
    fetchTopic({ silent: true });
  }, [fetchTopic]);

  const upvote = async (fid) => {
    try {
      const r = await api.post(`/atlas/facts/${fid}/helpful`);
      const delta = r.data.helpful_count_delta || 0;
      setNarrativeFacts((prev) => prev.map((f) => f.id === fid
        ? { ...f, helpful_count: Math.max(0, (f.helpful_count || 0) + delta) } : f));
      setAdvisorTipFacts((prev) => prev.map((f) => f.id === fid
        ? { ...f, helpful_count: Math.max(0, (f.helpful_count || 0) + delta) } : f));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Vote failed.");
    }
  };

  // (2026-08-04) Bug 3 — inline edit + delete on fact cards.
  // Both handlers refresh the panel silently on success so scroll
  // position + open nodes are preserved.
  const isAtlasAdmin = React.useMemo(() =>
    roles.includes("atlas_admin") || user?.role === "admin",
    [roles, user]);

  // Verification badge polling (2026-08-08). Refresh every 15 s so
  // an ingest apply from another tab / admin appears without a
  // manual reload. Fires an immediate refresh when the tab regains
  // visibility.
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const { data } = await api.get(`/atlas/topics/${slug}/verification`);
        if (!cancelled) setVerifyState(data);
      } catch { /* silent */ }
    };
    const timer = setInterval(fetchOnce, 15000);
    const onVis = () => { if (document.visibilityState === "visible") fetchOnce(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [slug]);

  const canManageFact = React.useCallback((f) => {
    if (!f) return false;
    if (isAtlasAdmin) return true;
    return f.contributor_advisor_id === user?.id && f.status === "pending_review";
  }, [isAtlasAdmin, user]);

  const saveFact = React.useCallback(async (fid, patch) => {
    try {
      const { data } = await api.patch(`/atlas/facts/${fid}`, patch);
      const updated = data?.fact;
      // Bubble update into both pools + narrative list.
      const applyUpdate = (list) => list.map((x) => x.id === fid ? { ...x, ...updated } : x);
      setAdvisorTipFacts((prev) => applyUpdate(prev));
      setTravelerReviews((prev) => applyUpdate(prev));
      setNarrativeFacts((prev) => applyUpdate(prev));
      // If display_type changed, silently refetch — cross-pool moves need it.
      if (patch.display_type && updated?.display_type !== patch.display_type) {
        // no-op; server rejected the change
      } else if (patch.display_type) {
        await fetchTopic({ silent: true });
      }
      toast.success("Fact updated.");
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed.");
      return false;
    }
  }, [fetchTopic]);

  const deleteFact = React.useCallback(async (fid) => {
    try {
      await api.delete(`/atlas/facts/${fid}`);
      setAdvisorTipFacts((prev) => prev.filter((x) => x.id !== fid));
      setTravelerReviews((prev) => prev.filter((x) => x.id !== fid));
      setNarrativeFacts((prev) => prev.filter((x) => x.id !== fid));
      setNarrativeCount((n) => Math.max(0, (n || 0) - 1));
      toast.success("Fact deleted.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed.");
    }
  }, []);

  const openEdit = async (opts = {}) => {
    try {
      const r = await api.get("/atlas/topics?limit=500");
      const list = r.data?.topics || [];
      const descendants = new Set([slug]);
      const queue = [slug];
      while (queue.length > 0) {
        const cur = queue.shift();
        try {
          const cr = await api.get(`/atlas/topics/${cur}/children`);
          for (const k of (cr.data.children || [])) {
            if (!descendants.has(k.slug)) { descendants.add(k.slug); queue.push(k.slug); }
          }
        } catch { /* noop */ }
      }
      setAllTopics(list.filter((p) => !descendants.has(p.slug)));
    } catch {
      setAllTopics([]);
    }
    setEditFocusField(opts.focusField || null);
    setShowEdit(true);
  };

  const ancestorSlugs = useMemo(() => ancestors.map((a) => a.slug), [ancestors]);
  const narrativeVisible = showAllNarrative ? narrativeFacts : narrativeFacts.slice(0, 20);
  const parentSlug = ancestorSlugs.length ? ancestorSlugs[ancestorSlugs.length - 1] : "";

  const goNav = (s) => nav(s ? `/atlas/topics/${s}` : "/atlas/topics");

  if (loading) {
    return (
      <div className="flex">
        <LeftRailTree currentSlug={slug} ancestorSlugs={ancestorSlugs}
          refreshKey={treeNonce} onNavigate={goNav} />
        <div className="flex-1 p-8 font-hand text-tb-navy/50">Loading…</div>
      </div>
    );
  }
  if (!topic) {
    return (
      <div className="flex min-h-full" data-testid="atlas-topic-not-found">
        <LeftRailTree currentSlug={slug} ancestorSlugs={ancestorSlugs}
          refreshKey={treeNonce} onNavigate={goNav} />
        <div className="flex-1 p-8">
          <div className="max-w-lg rounded-3xl border-2 border-dashed border-tb-navy/20 bg-tb-navy/[0.02] p-8 text-center">
            <div className="mx-auto h-14 w-14 rounded-full bg-tb-navy/10 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-tb-navy/60" />
            </div>
            <h2 className="mt-4 text-tb-navy text-2xl font-semibold"
                style={{ fontFamily: '"Playfair Display", serif' }}>
              Topic not found
            </h2>
            <p className="mt-2 font-hand text-tb-navy/60 text-sm">
              This topic may have been deleted, renamed, or never existed.
              The URL <code className="text-tb-navy/80">/atlas/topics/{slug}</code> doesn't resolve.
            </p>
            <button type="button" onClick={() => nav("/atlas/topics")}
              data-testid="atlas-topic-not-found-back"
              className="mt-5 inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm">
              <ArrowLeft className="w-4 h-4" /> Back to Topics
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full" data-testid="atlas-topic-focus">
      <LeftRailTree
        currentSlug={slug}
        ancestorSlugs={[...ancestorSlugs, slug]}
        onNavigate={goNav}
        refreshKey={treeNonce}
        onExternalRefresh={invalidateAtlasTree}
      />

      <div className="flex-1 min-w-0 p-4 md:p-8 max-w-4xl">
        <FocusBreadcrumb
          topic={topic}
          ancestors={ancestors}
          onBack={() => nav(parentSlug ? `/atlas/topics/${parentSlug}` : "/atlas/topics")}
          onHome={() => nav("/atlas/topics")}
          onCrumb={(s) => nav(`/atlas/topics/${s}`)}
        />

        <FocusHeader
          topic={topic}
          childCount={childCount}
          narrativeCount={narrativeCount}
          attachmentCount={attachments.length}
          onEdit={() => openEdit()}
          onDelete={() => setShowDelete(true)}
          onMove={() => setShowMove(true)}
          onCategoryClick={() => openEdit({ focusField: "entity_type" })}
          onAddSubtopic={() => setShowChild(true)}
          onUpload={() => setShowUpload(true)}
          isAdmin={isAdmin}
          verification={verifyState}
          onVerified={(fresh) => setVerifyState((prev) => ({ ...(prev || {}), ...fresh }))}
        />
        <UploadDocumentModal
          open={showUpload}
          onClose={() => setShowUpload(false)}
          currentSlug={topic?.slug || ""}
          currentName={topic?.name || ""} />

        {(activePromos.length > 0 || activeNotices.length > 0) && (
          <EphemeralBanners promos={activePromos} notices={activeNotices} />
        )}

        <DuplicateAlertPill
          parent={topic}
          children={children}
          onReview={() => setShowMerge(true)}
        />

        {/* 2026-08-10 · Layout reorder — Attachments → Attributes →
            Narrative facts → Sub-topics. LiveAccommodationsStrip
            unmounted from the folder detail page; still used by
            AtlasSmartCompareFiltered.jsx. */}
        <div className="mt-8">
          <h2 className="text-tb-navy text-lg font-semibold mb-3" style={{ fontFamily: '"Playfair Display", serif' }}>
            Attachments
          </h2>
          <AttachmentsStrip
            topicSlug={slug}
            attachments={attachments}
            onChange={(u) => setAttachments((prev) => typeof u === "function" ? u(prev) : u)}
          />
        </div>

        <div className="mt-8">
          <StructuredAttributesPanel slug={slug} />
        </div>

        <NarrativeFactsSection
          currentSlug={topic.slug}
          facts={narrativeFacts}
          visible={narrativeVisible}
          onUpvote={upvote}
          onShowAll={() => setShowAllNarrative(true)}
          onAdd={() => setShowAddNarrative(true)}
          canManage={canManageFact}
          onSave={saveFact}
          onDelete={deleteFact}
        />

        {/* K2-relatedlookup (2026-08-05) — reverse lookup by
            relationship. Hidden entirely when nothing points here. */}
        <RelatedSection related={related} />

        {/* K2-discover (2026-08-05) — two-hop discovery widget. */}
        <DiscoverSection discover={discover} sourceName={topic.name} />

        <div className="mt-8">
          <div className="flex items-baseline justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-tb-navy text-lg font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
                Sub-topics
              </h2>
              <p className="mt-0.5 font-hand text-tb-navy/50 text-[11px]">
                Nested topics live under <b>{topic.name}</b>. Category and entity type
                are inferred automatically.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {children.length > 0 && (
                <button type="button"
                  onClick={() => {
                    setSelectMode((v) => !v);
                    setSelectedChildren(new Set());
                  }}
                  data-testid="atlas-topic-select-mode-toggle"
                  className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full border font-hand text-xs transition ${
                    selectMode
                      ? "border-tb-navy bg-tb-navy text-tb-gold-bright"
                      : "border-tb-navy/25 text-tb-navy hover:bg-tb-navy/5"
                  }`}>
                  {selectMode
                    ? <><CheckSquare className="w-3.5 h-3.5" /> Done</>
                    : <><Square className="w-3.5 h-3.5" /> Select</>}
                </button>
              )}
              <button type="button"
                onClick={() => setShowChild(true)}
                data-testid="atlas-topic-add-child"
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm shadow-sm hover:shadow transition">
                <Plus className="w-4 h-4" /> Add sub-topic
              </button>
            </div>
          </div>
          <ChildrenGrid
            children={children}
            onOpen={(s) => nav(`/atlas/topics/${s}`)}
            onAdd={() => setShowChild(true)}
            parentName={topic.name}
            parentTopic={topic}
            childDepth={(ancestors?.length || 0) + 1}
            selectMode={selectMode}
            selectedSet={selectedChildren}
            onToggleSelect={(s) => setSelectedChildren((prev) => {
              const next = new Set(prev);
              if (next.has(s)) next.delete(s); else next.add(s);
              return next;
            })}
          />
          {selectMode && selectedChildren.size > 0 && (
            <BulkSelectionBar
              count={selectedChildren.size}
              onMove={() => setShowBulkMove(true)}
              onClear={() => setSelectedChildren(new Set())}
            />
          )}
        </div>

        {advisorTipFacts.length > 0 && (
          <AdvisorTipsSection tips={advisorTipFacts} onUpvote={upvote}
            canManage={canManageFact} onSave={saveFact} onDelete={deleteFact} />
        )}

        {travelerReviews.length > 0 && (
          <div className="mt-8" data-testid="atlas-topic-traveler-reviews">
            <h2 className="text-tb-navy text-lg font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
              From our travelers
            </h2>
            <div className="mt-3 space-y-2">
              {travelerReviews.slice(0, 10).map((r) => (
                <div key={r.id} className="rounded-xl border border-tb-navy/15 bg-white p-3">
                  <p className="font-hand text-tb-navy text-sm">{r.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {myPending.length > 0 && (
          <div className="mt-8" data-testid="atlas-topic-my-pending">
            <h3 className="font-hand text-tb-navy/70 text-[11px] uppercase tracking-widest">
              Your pending contributions on this topic
            </h3>
            <div className="mt-2 space-y-2">
              {myPending.map((f) => (
                <div key={f.id} className="rounded-xl border border-amber-200 bg-amber-50/40 p-3">
                  <p className="font-hand text-tb-navy text-sm whitespace-pre-wrap">{f.text}</p>
                  <div className="mt-1 font-hand text-[10px] text-amber-700 uppercase tracking-widest">Pending review</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <TopicFormModal
        mode="edit"
        open={showEdit}
        initial={topic}
        parentCandidates={allTopics}
        focusField={editFocusField}
        onClose={() => { setShowEdit(false); setEditFocusField(null); }}
        onSaved={(updated) => {
          setShowEdit(false);
          setEditFocusField(null);
          // Msg 586 · P0 bug fix — invalidate the left rail tree so
          // renames (e.g. "Ships" → "Fleet"), entity_type changes,
          // category swaps, or any other edit propagate to the
          // sidebar within ~1s without a manual reload.
          invalidateAtlasTree();
          if (updated?.slug && updated.slug !== slug) nav(`/atlas/topics/${updated.slug}`);
          else load();
        }}
      />
      <MoveTopicModal
        open={showMove}
        topic={topic}
        ancestors={ancestors}
        onClose={() => setShowMove(false)}
        onMoved={({ newParent }) => {
          setShowMove(false);
          invalidateAtlasTree();
          load();
          void newParent;
        }}
      />
      <MoveTopicModal
        open={showBulkMove}
        topic={topic}
        ancestors={ancestors}
        bulkSlugs={Array.from(selectedChildren)}
        onClose={() => setShowBulkMove(false)}
        onMoved={() => {
          setShowBulkMove(false);
          setSelectMode(false);
          setSelectedChildren(new Set());
          invalidateAtlasTree();
          load();
        }}
      />
      <MergeDuplicatesModal
        open={showMerge}
        parent={topic}
        children={children}
        onClose={() => setShowMerge(false)}
        onMerged={() => {
          setShowMerge(false);
          invalidateAtlasTree();
          load();
        }}
      />
      <DeleteStrategyModal
        open={showDelete}
        topic={topic}
        childCount={childCount}
        factCount={narrativeCount}
        onClose={() => setShowDelete(false)}
        onDeleted={() => {
          setShowDelete(false);
          invalidateAtlasTree();
          nav(parentSlug ? `/atlas/topics/${parentSlug}` : "/atlas/topics");
        }}
      />
      <TopicFormModal
        mode="create"
        open={showChild}
        parentSlug={slug}
        onClose={() => setShowChild(false)}
        onSaved={(created) => {
          // Msg 574 · P0 bug fix — invalidate the left rail tree so
          // the newly-created sub-topic appears in the sidebar
          // immediately (previously the cache held the pre-create
          // list). Also `load()` so the focus view's own sub-topic
          // grid refreshes.
          setShowChild(false);
          invalidateAtlasTree();
          load();
          void created;
        }}
      />
      <AddNarrativeFactModal
        open={showAddNarrative}
        topicSlug={topic?.slug}
        topicName={topic?.name}
        onClose={() => setShowAddNarrative(false)}
        onSaved={() => {
          setShowAddNarrative(false);
          load();
        }}
      />
    </div>
  );
}


// ─── Focus view sub-components (kept small so react-refresh doesn't choke) ───

function FocusBreadcrumb({ topic, ancestors, onBack, onHome, onCrumb }) {
  // Msg 581 · K.4 — progressive weight per crumb: Home = medium,
  // intermediate = medium/semibold, current (last) = bold. Reinforces
  // depth visually without changing color palette.
  return (
    <div className="flex items-center gap-2 mb-3">
      <button type="button" onClick={onBack}
        data-testid="atlas-topic-back"
        className="md:hidden h-9 w-9 shrink-0 inline-flex items-center justify-center rounded-full border border-tb-navy/15 text-tb-navy/70">
        <ArrowLeft className="w-4 h-4" />
      </button>
      <nav className="flex-1 min-w-0 overflow-x-auto" data-testid="atlas-topic-breadcrumb">
        <ol className="inline-flex items-center gap-1 whitespace-nowrap">
          <li>
            <button type="button" onClick={onHome}
              data-testid="breadcrumb-home"
              className="inline-flex items-center gap-1 font-hand text-xs text-tb-navy/60 hover:text-tb-navy font-bold">
              <Home className="w-3 h-3" /> Topics
            </button>
          </li>
          {ancestors.map((a, i) => (
            <BreadcrumbSegment key={a.slug} slug={a.slug} name={a.name}
              depth={i + 1} totalDepth={ancestors.length + 1} onCrumb={onCrumb} />
          ))}
          <ChevronRight className="w-3 h-3 text-tb-navy/30" />
          <li>
            <span data-testid={`breadcrumb-current-${topic.slug}`}
                  className="font-hand text-sm text-tb-navy font-bold">{topic.name}</span>
          </li>
        </ol>
      </nav>
    </div>
  );
}

function BreadcrumbSegment({ slug, name, depth, totalDepth, onCrumb }) {
  // Msg 581 · K.4 — root crumbs (depth 1) are bold; intermediates use
  // medium weight; the very last intermediate before current stays
  // semibold to hint at proximity.
  const weight =
    depth === 1 ? "font-bold text-xs" :
    depth === (totalDepth - 1) ? "font-semibold text-xs" :
    "font-medium text-xs";
  return (
    <>
      <ChevronRight className="w-3 h-3 text-tb-navy/30" />
      <li>
        <button type="button" onClick={() => onCrumb(slug)}
          data-testid={`breadcrumb-${slug}`}
          data-depth={depth}
          className={`font-hand ${weight} text-tb-navy/60 hover:text-tb-navy`}>
          {name}
        </button>
      </li>
    </>
  );
}


function FocusHeader({ topic, childCount, narrativeCount, attachmentCount, onEdit, onDelete, onMove, onCategoryClick, onAddSubtopic, isAdmin, onUpload, verification, onVerified }) {
  // Msg 591 · P0 fix (Issue 2) — the chip now reads from the
  // user-defined categories cache via `category_id`. Retired the
  // entity_type / free-text `category` fallbacks that were showing
  // stale values like "Other" or "Root Group" post-Phase A.
  const categories = useAtlasCategories();
  const cat = topic.category_id
    ? categories.find((c) => c.id === topic.category_id)
    : null;
  const categoryDisplay = cat ? cat.name : "—";
  return (
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button"
            onClick={onCategoryClick}
            data-testid="topic-header-category-chip"
            aria-label={`Category: ${categoryDisplay}. Click to edit.`}
            title="Click to edit category / entity type"
            className="inline-flex items-center gap-1.5 h-6 pl-2 pr-2.5 rounded-full border border-tb-gold-bright/60 bg-tb-gold-bright/15 text-tb-navy font-hand text-[10px] uppercase tracking-widest hover:bg-tb-gold-bright/25 hover:border-tb-gold-bright transition"
          >
            <EntityIcon categoryId={topic.category_id} className="w-3 h-3" />
            <span data-testid="topic-header-category-label">
              Category: <b>{categoryDisplay}</b>
            </span>
            <Pencil className="w-2.5 h-2.5 opacity-60" aria-hidden="true" />
          </button>
          {topic.template_scaffold && (
            <span data-testid="topic-header-template-chip"
              title={`Template folder scaffolded from the ${humanize(topic.scaffold_template_key || "")} template.`}
              className="inline-flex items-center gap-1.5 h-6 pl-2 pr-2.5 rounded-full border border-tb-navy/25 bg-white text-tb-navy/70 font-hand text-[10px] uppercase tracking-widest">
              <Folder className="w-3 h-3" />
              Template folder
            </span>
          )}
          {topic.template_key && !topic.template_scaffold && (
            isAdmin ? (
              <Link to={`/atlas/topics?view=templates&open=${encodeURIComponent(topic.template_key)}`}
                data-testid="topic-header-template-owner-chip"
                title={`Template applied: ${humanize(topic.template_key)}. Click to edit the template.`}
                className="inline-flex items-center gap-1.5 h-6 pl-2 pr-2.5 rounded-full border border-tb-gold-bright/40 bg-tb-gold-bright/10 text-tb-navy/80 font-hand text-[10px] uppercase tracking-widest hover:bg-tb-gold-bright/20">
                <Folder className="w-3 h-3" />
                Uses template: {humanize(topic.template_key)}
                <ArrowUpRight className="w-2.5 h-2.5 opacity-60" />
              </Link>
            ) : (
              <span data-testid="topic-header-template-owner-chip"
                title={`Template applied: ${humanize(topic.template_key)}. New sub-topics under this node get standard scaffolded folders.`}
                className="inline-flex items-center gap-1.5 h-6 pl-2 pr-2.5 rounded-full border border-tb-gold-bright/40 bg-tb-gold-bright/10 text-tb-navy/80 font-hand text-[10px] uppercase tracking-widest">
                <Folder className="w-3 h-3" />
                Template: {humanize(topic.template_key)}
              </span>
            )
          )}
          {topic.status && topic.status !== "canonical" && (
            <Chip tone="ghost" testId="topic-header-status">{humanize(topic.status)}</Chip>
          )}
        </div>
        <h1 className="mt-2 text-tb-navy text-3xl md:text-4xl font-semibold leading-tight flex items-center gap-3 flex-wrap"
            style={{ fontFamily: '"Playfair Display", serif' }}
            data-testid="atlas-topic-name">
          <span>{topic.name}</span>
          {/* Verification badge — larger, sits next to the folder
              title. Admin-clickable to flip unverified/needs_review →
              verified. Read-only for other roles. */}
          <VerificationBadge
            slug={topic.slug}
            status={verification?.verification_status || "unverified"}
            verifiedAt={verification?.verified_at}
            verifiedByName={verification?.verified_by_name}
            contentChangedAt={verification?.content_changed_at}
            isAdmin={isAdmin}
            size="md"
            onVerified={onVerified}
          />
        </h1>
        {topic.description && (
          <p className="mt-2 font-hand text-tb-navy/70 text-sm max-w-2xl">{topic.description}</p>
        )}
        <div className="mt-2 flex items-center gap-4 flex-wrap">
          <CountPill icon={Folder} count={childCount} label={childCount === 1 ? "sub-topic" : "sub-topics"} testId="topic-header-child-count" />
          <CountPill icon={MessageSquareQuote} count={narrativeCount} label="facts" testId="topic-header-fact-count" />
          <CountPill icon={Paperclip} count={attachmentCount} label={attachmentCount === 1 ? "attachment" : "attachments"} testId="topic-header-attachment-count" />
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
        <button type="button" onClick={onAddSubtopic}
          data-testid="topic-header-add-subtopic"
          className="h-9 px-3.5 inline-flex items-center gap-1.5 rounded-full bg-tb-navy text-tb-gold-bright hover:bg-tb-navy/90 font-hand text-xs shadow-sm">
          <Plus className="w-3.5 h-3.5" /> Add sub-topic
        </button>
        {isAdmin && (
          <button type="button" onClick={onUpload}
            data-testid="topic-header-upload"
            title="Upload a document and extract facts into this folder"
            className="h-9 px-3 inline-flex items-center gap-1.5 rounded-full border border-tb-navy/25 bg-white text-tb-navy hover:bg-tb-navy/5 font-hand text-xs">
            <Upload className="w-3.5 h-3.5" /> Upload document
          </button>
        )}
        <button type="button" onClick={onMove}
          data-testid="topic-header-move"
          className="h-9 px-3 inline-flex items-center gap-1.5 rounded-full border border-tb-navy/25 text-tb-navy hover:bg-tb-navy/5 font-hand text-xs">
          <MoveIcon className="w-3.5 h-3.5" /> Move
        </button>
        <button type="button" onClick={onEdit}
          data-testid="topic-header-edit"
          className="h-9 px-3 inline-flex items-center gap-1.5 rounded-full border border-tb-navy/20 text-tb-navy hover:bg-tb-navy/5 font-hand text-xs">
          <Pencil className="w-3.5 h-3.5" /> Edit
        </button>
        <button type="button" onClick={onDelete}
          data-testid="topic-header-delete"
          className="h-9 px-3 inline-flex items-center gap-1.5 rounded-full border border-red-300 text-red-700 hover:bg-red-50 font-hand text-xs">
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </button>
      </div>
    </div>
  );
}


function BulkSelectionBar({ count, onMove, onClear }) {
  return (
    <div className="mt-3 rounded-2xl border border-tb-navy/20 bg-white shadow-sm p-3 flex items-center justify-between gap-3 flex-wrap sticky bottom-3 z-10"
         data-testid="atlas-topic-bulk-bar">
      <div className="font-hand text-sm text-tb-navy">
        <b>{count}</b> selected
      </div>
      <div className="flex items-center gap-2">
        <button type="button" onClick={onClear}
          data-testid="atlas-topic-bulk-clear"
          className="h-9 px-3 inline-flex items-center gap-1.5 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-xs hover:bg-tb-navy/5">
          Clear
        </button>
        <button type="button" onClick={onMove}
          data-testid="atlas-topic-bulk-move"
          className="h-9 px-4 inline-flex items-center gap-1.5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs">
          <MoveIcon className="w-3.5 h-3.5" /> Move {count} selected topic{count === 1 ? "" : "s"} to…
        </button>
      </div>
    </div>
  );
}


function EphemeralBanners({ promos, notices }) {
  return (
    <div className="mt-6" data-testid="atlas-topic-ephemeral">
      <h2 className="text-tb-navy text-lg font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
        Current promos &amp; notices
      </h2>
      <div className="mt-2 space-y-2">
        {promos.map((p) => (
          <div key={p.id} data-testid={`atlas-topic-promo-${p.id}`}
               className="rounded-xl border border-red-300 bg-red-50/60 p-3">
            <p className="font-hand text-tb-navy text-sm">{p.text}</p>
          </div>
        ))}
        {notices.map((n) => (
          <div key={n.id} data-testid={`atlas-topic-notice-${n.id}`}
               className={"rounded-xl border p-3 " +
                 (n.severity === "critical" ? "border-red-400 bg-red-50" :
                  n.severity === "warning" ? "border-amber-300 bg-amber-50/70" :
                  "border-blue-200 bg-blue-50/60")}>
            <p className="font-hand text-tb-navy text-sm">{n.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}


// Msg 581 · K.3 — depth-scaled card typography + subtle background tint
// mirrors the LeftRailTree depth scale so grandchildren look visibly
// smaller than root-level cards.
const _CARD_DEPTH_SCALE = [
  // depth 0 — root topic's own card (shouldn't normally appear in a
  // ChildrenGrid but here for safety).
  { title: "text-lg", weight: "font-bold",    tint: "bg-white",             pad: "p-5" },
  // depth 1 — direct children of a root
  { title: "text-base", weight: "font-semibold", tint: "bg-white",           pad: "p-4" },
  // depth 2
  { title: "text-sm",   weight: "font-semibold", tint: "bg-slate-50/60",     pad: "p-4" },
  // depth 3+
  { title: "text-sm",   weight: "font-medium",   tint: "bg-slate-100/60",    pad: "p-3.5" },
];
function _cardDepthTypo(depth) {
  const idx = Math.min(Math.max(depth, 0), _CARD_DEPTH_SCALE.length - 1);
  return _CARD_DEPTH_SCALE[idx];
}


function ChildrenGrid({ children, onOpen, onAdd, parentName, parentTopic = null,
                        childDepth = 1,
                        selectMode = false, selectedSet, onToggleSelect }) {
  if (children.length === 0) {
    const isScaffold = !!parentTopic?.template_scaffold;
    const focusNoteInput = () => {
      try {
        const el = document.querySelector('[data-testid="note-capture-input"]');
        if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus?.(); }
      } catch { /* noop */ }
    };
    if (isScaffold) {
      return (
        <div className="mt-3 grid grid-cols-1 gap-3" data-testid="atlas-topic-children-grid">
          <div data-testid="atlas-topic-scaffold-empty"
            className="rounded-2xl border-2 border-dashed border-tb-navy/25 hover:border-tb-navy/40 transition p-5 bg-white">
            <div className="flex items-start gap-3">
              <span className="h-10 w-10 rounded-full bg-tb-gold-bright/20 text-tb-navy inline-flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5" />
              </span>
              <div className="min-w-0">
                <div className="text-tb-navy text-base font-semibold"
                     style={{ fontFamily: '"Playfair Display", serif' }}>
                  Nothing here yet
                </div>
                <p className="mt-1 font-hand text-[12px] text-tb-navy/70">
                  This is a template folder for {parentName ? <b>{parentName}</b> : "this topic"}.
                  {" "}Add resorts, tips, notes, or upload attachments here — everything you save
                  becomes searchable across TravelBio.
                </p>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <button type="button" onClick={onAdd}
                    data-testid="atlas-topic-scaffold-add-child"
                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs">
                    <Plus className="w-3.5 h-3.5" /> Add first sub-topic
                  </button>
                  <button type="button" onClick={focusNoteInput}
                    data-testid="atlas-topic-scaffold-add-note"
                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-xs hover:bg-tb-navy/5">
                    <Plus className="w-3.5 h-3.5" /> Add note
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3"
           data-testid="atlas-topic-children-grid">
        <button type="button"
          onClick={onAdd}
          data-testid="atlas-topic-children-empty-cta"
          className="text-left rounded-2xl border-2 border-dashed border-tb-navy/25 hover:border-tb-navy hover:bg-tb-navy/[0.03] transition p-5 bg-white group focus:outline-none focus:ring-2 focus:ring-tb-navy/40"
        >
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-full bg-tb-navy/10 text-tb-navy inline-flex items-center justify-center group-hover:bg-tb-navy group-hover:text-tb-gold-bright transition">
              <Plus className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <div className="text-tb-navy text-sm font-semibold"
                   style={{ fontFamily: '"Playfair Display", serif' }}>
                Add the first sub-topic
              </div>
              <div className="font-hand text-[11px] text-tb-navy/60 mt-0.5">
                {parentName ? <>Nest a specific topic under <b>{parentName}</b>.</>
                            : "Nest a specific topic under this node."}
                {" "}Category is inferred automatically.
              </div>
            </div>
          </div>
        </button>
      </div>
    );
  }
  return (
    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="atlas-topic-children-grid">
      {children.map((c) => (
        <ChildTopicCard key={c.slug} c={c}
          depth={childDepth}
          onOpen={() => onOpen(c.slug)}
          selectMode={selectMode}
          selected={selectedSet ? selectedSet.has(c.slug) : false}
          onToggleSelect={() => onToggleSelect?.(c.slug)}
        />
      ))}
    </div>
  );
}


function ChildTopicCard({ c, depth = 1, onOpen, selectMode = false, selected = false, onToggleSelect }) {
  const typo = _cardDepthTypo(depth);
  const handleClick = () => {
    if (selectMode) onToggleSelect?.();
    else onOpen();
  };
  const ringCls = selected
    ? "border-tb-navy ring-2 ring-tb-navy/40"
    : "border-tb-navy/15 hover:border-tb-navy";
  return (
    <button type="button" onClick={handleClick}
      data-testid={`atlas-topic-child-card-${c.slug}`}
      data-depth={depth}
      className={`text-left rounded-2xl border transition ${typo.pad} ${typo.tint} ${ringCls}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          {selectMode && (
            <span data-testid={`atlas-topic-child-checkbox-${c.slug}`}
              className={`mt-1 h-4 w-4 rounded border shrink-0 inline-flex items-center justify-center ${
                selected ? "border-tb-navy bg-tb-navy text-tb-gold-bright" : "border-tb-navy/30 bg-white"
              }`}>
              {selected && <CheckSquare className="w-3 h-3" />}
            </span>
          )}
          <div className="min-w-0">
            <h3 className={`text-tb-navy truncate ${typo.title} ${typo.weight}`}
                style={{ fontFamily: '"Playfair Display", serif' }}>{c.name}</h3>
            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
              {c.entity_type && c.entity_type !== "other" && (
                <Chip tone="gold">
                  <EntityIcon categoryId={c.category_id} className="w-3 h-3" />
                  {humanize(c.entity_type)}
                </Chip>
              )}
            </div>
          </div>
        </div>
        {!selectMode && <ArrowUpRight className="w-3.5 h-3.5 text-tb-navy/40 shrink-0" />}
      </div>
      <div className="mt-2 font-hand text-[11px] text-tb-navy/60">
        {c.fact_count || 0} fact{(c.fact_count || 0) === 1 ? "" : "s"}
      </div>
    </button>
  );
}


function AdvisorTipsSection({ tips, onUpvote, canManage, onSave, onDelete }) {
  return (
    <div className="mt-8" data-testid="atlas-topic-tips">
      <h2 className="text-tb-navy text-lg font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
        TravelBio Advisor Tips
      </h2>
      <div className="mt-3 space-y-3">
        {tips.map((f) => (
          <AdvisorTipCard key={f.id} f={f} onUpvote={() => onUpvote(f.id)}
            canManage={canManage?.(f)} onSave={onSave} onDelete={onDelete} />
        ))}
      </div>
    </div>
  );
}


function AdvisorTipCard({ f, onUpvote, canManage, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(f.text || "");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const t = draft.trim();
    if (t.length < 3) return;
    setSaving(true);
    const ok = await onSave?.(f.id, { text: t });
    setSaving(false);
    if (ok) setEditing(false);
  };
  const remove = async () => {
    if (!window.confirm(`Delete this advisor tip?\n\n"${(f.text || "").slice(0, 100)}"`)) return;
    await onDelete?.(f.id);
  };
  return (
    <div data-testid={`atlas-topic-tip-${f.id}`}
         className="rounded-xl border border-tb-gold-bright/60 bg-tb-gold-bright/10 p-4">
      <div className="flex items-start gap-2">
        <span className="text-[9px] uppercase tracking-widest font-hand rounded-full bg-tb-navy text-tb-gold-bright px-2 py-0.5 flex-shrink-0 mt-0.5">
          Advisor tip
        </span>
        <div className="flex-1">
          {editing ? (
            <div>
              <TextareaAutosize value={draft} onChange={(e) => setDraft(e.target.value)}
                disabled={saving} minRows={4} maxRows={30}
                data-testid={`atlas-topic-tip-edit-textarea-${f.id}`}
                className="w-full px-2 py-1.5 rounded-lg border border-tb-navy/25 outline-none focus:border-tb-navy font-hand text-sm bg-white text-tb-navy whitespace-pre-wrap resize-none" />
              <div className="mt-2 flex items-center gap-2 justify-end">
                <button type="button" onClick={() => { setDraft(f.text || ""); setEditing(false); }}
                  disabled={saving}
                  className="h-7 px-3 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-xs">
                  Cancel
                </button>
                <button type="button" onClick={submit} disabled={saving || draft.trim().length < 3}
                  data-testid={`atlas-topic-tip-edit-save-${f.id}`}
                  className="h-7 px-3 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs disabled:opacity-50">
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          ) : (
            <p className="font-hand text-tb-navy text-sm whitespace-pre-wrap">{f.text}</p>
          )}
          <div className="mt-2 flex items-center gap-2 font-hand text-[10px] text-tb-navy/60">
            {f.contributor_advisor_name && <span>by {f.contributor_advisor_name}</span>}
            {canManage && !editing && (
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => setEditing(true)}
                  aria-label="Edit"
                  data-testid={`atlas-topic-tip-edit-${f.id}`}
                  className="h-6 w-6 inline-flex items-center justify-center rounded text-tb-navy/60 hover:bg-tb-navy/5">
                  <Pencil className="w-3 h-3" />
                </button>
                <button type="button" onClick={remove}
                  aria-label="Delete"
                  data-testid={`atlas-topic-tip-delete-${f.id}`}
                  className="h-6 w-6 inline-flex items-center justify-center rounded text-red-700/70 hover:bg-red-50">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            )}
            <button type="button" onClick={onUpvote}
              className="ml-auto inline-flex items-center gap-1 text-[11px] font-hand text-tb-navy/70 hover:text-tb-navy border border-tb-navy/15 rounded-full px-2 py-0.5">
              <ThumbsUp className="w-3 h-3" /> {f.helpful_count || 0}
            </button>
            <FactSourceInfoBadge fact={f} testId={`tip-${f.id}`} />
          </div>
        </div>
      </div>
    </div>
  );
}


function NarrativeFactsSection({ currentSlug, facts, visible, onUpvote, onShowAll, onAdd, canManage, onSave, onDelete }) {
  return (
    <div className="mt-8">
      <div className="flex items-baseline justify-between">
        <h2 className="text-tb-navy text-lg font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
          Narrative facts
        </h2>
        <button type="button" onClick={onAdd}
          data-testid="atlas-topic-add-narrative"
          className="inline-flex items-center gap-1 h-8 px-3 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-xs hover:bg-tb-navy/5">
          <Plus className="w-3 h-3" /> Add fact
        </button>
      </div>
      {facts.length === 0 && (
        <div className="mt-2 rounded-xl border border-dashed border-tb-navy/20 bg-tb-navy/[0.02] p-6 text-center font-hand text-tb-navy/50 text-sm">
          No narrative facts here yet.
        </div>
      )}
      <div className="mt-3 space-y-2" data-testid="atlas-topic-facts">
        {visible.map((f) => (
          <NarrativeFactCard key={f.id} f={f} currentSlug={currentSlug}
            onUpvote={() => onUpvote(f.id)}
            canManage={canManage?.(f)} onSave={onSave} onDelete={onDelete} />
        ))}
      </div>
      {facts.length > visible.length && (
        <button type="button" onClick={onShowAll}
          className="mt-3 inline-flex items-center gap-1 font-hand text-xs text-tb-navy/70 hover:text-tb-navy underline underline-offset-2">
          Show all {facts.length}
        </button>
      )}
    </div>
  );
}


function NarrativeFactCard({ f, currentSlug, onUpvote, canManage, onSave, onDelete }) {
  const otherSlugs = (f.topic_slugs || []).filter((s) => s !== currentSlug).slice(0, 5);
  const sourceLabel = f.source === "advisor_screenshot" ? "screenshot"
    : f.source === "admin_curated" ? "curated" : "typed";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(f.text || "");
  const [saving, setSaving] = useState(false);
  const isAdvisorTip = f.display_type === "advisor_tip";
  // 2026-08-06 — Convert-to-attribute inline form + advisor-tip toggle.
  // Convert-to-narrative isn't needed here (this IS a narrative card).
  const [convOpen, setConvOpen]   = useState(false);
  const [convKey, setConvKey]     = useState("");
  const [convValue, setConvValue] = useState(f.text || "");
  const submit = async () => {
    const t = draft.trim();
    if (t.length < 3) return;
    setSaving(true);
    const ok = await onSave?.(f.id, { text: t });
    setSaving(false);
    if (ok) setEditing(false);
  };
  const toggleAdvisorTip = async () => {
    await onSave?.(f.id, {
      display_type: isAdvisorTip ? "narrative" : "advisor_tip",
    });
  };
  const convertToAttribute = async () => {
    const k = convKey.trim(), v = convValue.trim();
    if (!k || !v) return;
    const ok = await onSave?.(f.id, {
      display_type: "attribute",
      attribute_key: k,
      attribute_value: v,
      text: v,
    });
    if (ok) setConvOpen(false);
  };
  const remove = async () => {
    if (!window.confirm(`Delete this narrative fact?\n\n"${(f.text || "").slice(0, 100)}"`)) return;
    await onDelete?.(f.id);
  };
  return (
    <div className="rounded-xl border border-tb-navy/15 bg-white p-3" data-testid={`atlas-topic-fact-${f.id}`}>
      {editing ? (
        <div>
          <TextareaAutosize value={draft} onChange={(e) => setDraft(e.target.value)}
            disabled={saving} minRows={4} maxRows={30}
            data-testid={`atlas-topic-fact-edit-textarea-${f.id}`}
            className="w-full px-2 py-1.5 rounded-lg border border-tb-navy/25 outline-none focus:border-tb-navy font-hand text-sm bg-white text-tb-navy whitespace-pre-wrap resize-none" />
          <div className="mt-2 flex items-center gap-2 justify-end">
            <button type="button" onClick={() => { setDraft(f.text || ""); setEditing(false); }}
              disabled={saving}
              className="h-7 px-3 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-xs">
              Cancel
            </button>
            <button type="button" onClick={submit} disabled={saving || draft.trim().length < 3}
              data-testid={`atlas-topic-fact-edit-save-${f.id}`}
              className="h-7 px-3 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs disabled:opacity-50">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      ) : (
        <p className="font-hand text-tb-navy text-sm whitespace-pre-wrap">{f.text}</p>
      )}
      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
        {otherSlugs.map((s) => (
          <Link key={s} to={`/atlas/topics/${s}`}
            className="text-[10px] font-hand px-2 py-0.5 rounded-full border border-tb-navy/20 text-tb-navy/70 hover:bg-tb-navy/5">
            #{s}
          </Link>
        ))}
        <span className="text-[10px] font-hand text-tb-navy/40 ml-auto">
          {sourceLabel}{f.contributor_advisor_name ? ` · ${f.contributor_advisor_name}` : ""}
        </span>
        {canManage && !editing && (
          <div className="flex items-center gap-1">
            <label className="inline-flex items-center gap-1 text-[10px] font-hand text-amber-800 cursor-pointer"
                   title="Highlight as Advisor Tip"
                   data-testid={`atlas-topic-fact-advisor-tip-${f.id}`}>
              <input type="checkbox"
                checked={isAdvisorTip}
                onChange={toggleAdvisorTip}
                className="h-3 w-3 accent-amber-500" />
              <span>Tip 💡</span>
            </label>
            <button type="button" onClick={() => setConvOpen((v) => !v)}
              aria-label="Convert to attribute"
              title="Convert to attribute"
              data-testid={`atlas-topic-fact-convert-${f.id}`}
              className="h-6 w-6 inline-flex items-center justify-center rounded text-tb-navy/60 hover:bg-tb-navy/5">
              <ArrowRightLeft className="w-3 h-3" />
            </button>
            <button type="button" onClick={() => setEditing(true)}
              aria-label="Edit"
              data-testid={`atlas-topic-fact-edit-${f.id}`}
              className="h-6 w-6 inline-flex items-center justify-center rounded text-tb-navy/60 hover:bg-tb-navy/5">
              <Pencil className="w-3 h-3" />
            </button>
            <button type="button" onClick={remove}
              aria-label="Delete"
              data-testid={`atlas-topic-fact-delete-${f.id}`}
              className="h-6 w-6 inline-flex items-center justify-center rounded text-red-700/70 hover:bg-red-50">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}
        <button type="button" onClick={onUpvote}
          className="inline-flex items-center gap-1 text-[11px] font-hand text-tb-navy/70 hover:text-tb-navy border border-tb-navy/15 rounded-full px-2 py-0.5">
          <ThumbsUp className="w-3 h-3" /> {f.helpful_count || 0}
        </button>
        <FactSourceInfoBadge fact={f} testId={`fact-${f.id}`} />
      </div>
      {convOpen && (
        <div className="mt-2 p-2 rounded-lg border border-tb-navy/15 bg-tb-navy/[0.03]"
             data-testid={`atlas-topic-fact-convert-form-${f.id}`}>
          <div className="text-[11px] uppercase tracking-widest font-hand text-tb-navy/50 mb-1">
            Convert to attribute
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input type="text"
              value={convKey} onChange={(e) => setConvKey(e.target.value)}
              placeholder="Attribute name (e.g. Best For)"
              data-testid={`atlas-topic-fact-convert-key-${f.id}`}
              className="h-8 px-2 rounded border border-tb-navy/25 font-hand text-sm text-tb-navy" />
            <input type="text"
              value={convValue} onChange={(e) => setConvValue(e.target.value)}
              placeholder="Value"
              data-testid={`atlas-topic-fact-convert-value-${f.id}`}
              className="h-8 px-2 rounded border border-tb-navy/25 font-hand text-sm text-tb-navy" />
          </div>
          <div className="mt-2 flex items-center justify-end gap-1.5">
            <button type="button" onClick={() => setConvOpen(false)}
              className="h-7 px-3 rounded-full text-tb-navy/60 hover:text-tb-navy font-hand text-xs">
              Cancel
            </button>
            <button type="button" onClick={convertToAttribute}
              disabled={!convKey.trim() || !convValue.trim()}
              data-testid={`atlas-topic-fact-convert-save-${f.id}`}
              className="h-7 px-3 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs disabled:opacity-40">
              Convert
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// Msg 601 · P0 — inline "Add narrative fact" dialog. Replaces the
// old `/atlas/knowledge?tags=<slug>` navigation, which now bounces
// to `/atlas/topics` (route retired in App.js). Files a fact
// straight to the current topic via POST /api/atlas/facts —
// `topic_slugs: [slug]`, `source: "advisor_typed"`,
// `display_type: "narrative"`. Admin auto-approve (Msg 600) still
// applies — the fact lands as canonical for admin users.
// ─────────────────────────────────────────────────────────────
function AddNarrativeFactModal({ open, topicSlug, topicName, onClose, onSaved }) {
  const [text, setText] = useState("");
  // (2026-08-04) Bug 2 fix — explicit type selector. User's choice
  // wins over any LLM auto-classification. Default: narrative.
  const [displayType, setDisplayType] = useState("narrative");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!open) return;
    setText(""); setDisplayType("narrative"); setErr(null); setBusy(false);
  }, [open]);
  if (!open || !topicSlug) return null;
  const submit = async (e) => {
    e?.preventDefault?.();
    const txt = text.trim();
    if (txt.length < 6) {
      setErr("Text must be at least 6 characters.");
      return;
    }
    setBusy(true); setErr(null);
    try {
      await api.post("/atlas/facts", {
        text: txt,
        topic_slugs: [topicSlug],
        source: "advisor_typed",
        display_type: displayType,
      });
      const label = displayType === "advisor_tip"
        ? "advisor tip"
        : displayType === "traveler_review"
          ? "traveler review"
          : "narrative fact";
      toast.success(`Filed ${label} to ${topicName || topicSlug}.`);
      onSaved?.();
    } catch (ex) {
      setErr(formatApiErrorDetail(ex?.response?.data?.detail) || "Save failed.");
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-start md:items-center justify-center overflow-y-auto p-4"
         data-testid="add-narrative-fact-modal"
         onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <form onSubmit={submit}
        className="w-full max-w-lg rounded-3xl bg-white shadow-2xl border border-tb-navy/15 my-4">
        <div className="p-5 border-b border-tb-navy/10 flex items-center justify-between">
          <div>
            <h3 className="text-tb-navy text-xl font-semibold"
                style={{ fontFamily: '"Playfair Display", serif' }}>
              Add fact
            </h3>
            <p className="mt-1 font-hand text-tb-navy/60 text-xs">
              On <b>{topicName || topicSlug}</b>. Filed here only.
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={busy}
            data-testid="add-narrative-fact-close"
            className="h-8 w-8 rounded-full hover:bg-tb-navy/5 inline-flex items-center justify-center text-tb-navy/60">
            <CloseIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          {/* Type selector — user always has the final call. */}
          <div>
            <div className="text-[10px] font-hand uppercase tracking-widest text-tb-navy/50 mb-1.5">
              Type
            </div>
            <div className="grid grid-cols-2 gap-2" role="radiogroup"
                 data-testid="add-fact-type-group">
              {[
                { v: "narrative", label: "Narrative fact",
                  hint: "Structured / factual content", testid: "add-fact-type-narrative" },
                { v: "advisor_tip", label: "Advisor tip",
                  hint: "Subjective observation", testid: "add-fact-type-advisor-tip" },
              ].map((opt) => (
                <button key={opt.v} type="button"
                  onClick={() => setDisplayType(opt.v)}
                  disabled={busy}
                  data-testid={opt.testid}
                  aria-pressed={displayType === opt.v}
                  className={
                    "text-left px-3 py-2 rounded-xl border font-hand text-sm transition " +
                    (displayType === opt.v
                      ? "border-tb-navy bg-tb-navy text-tb-gold-bright"
                      : "border-tb-navy/20 bg-white text-tb-navy hover:border-tb-navy/40")
                  }>
                  <div className="font-semibold">{opt.label}</div>
                  <div className={
                    "text-[10px] " +
                    (displayType === opt.v ? "text-tb-gold-bright/80" : "text-tb-navy/50")
                  }>{opt.hint}</div>
                </button>
              ))}
            </div>
          </div>
          <TextareaAutosize
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`Share a ${displayType === "advisor_tip" ? "tip" : "fact"} about ${topicName || topicSlug}…`}
            minRows={6}
            maxRows={30}
            disabled={busy}
            data-testid="add-narrative-fact-text"
            className="w-full min-h-[120px] px-3 py-2 rounded-xl border border-tb-navy/20 outline-none focus:border-tb-navy font-hand text-sm bg-white text-tb-navy resize-none whitespace-pre-wrap"
          />
          {err && (
            <div className="rounded-lg border border-red-300 bg-red-50/70 text-red-800 font-hand text-xs px-3 py-2"
                 data-testid="add-narrative-fact-error">
              {err}
            </div>
          )}
        </div>
        <div className="p-4 border-t border-tb-navy/10 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy}
            data-testid="add-narrative-fact-cancel"
            className="h-10 px-4 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-sm hover:bg-tb-navy/5 disabled:opacity-50">
            Cancel
          </button>
          <button type="submit" disabled={busy || !text.trim()}
            data-testid="add-narrative-fact-submit"
            className="h-10 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm disabled:opacity-50 inline-flex items-center gap-1.5">
            <Plus className="w-4 h-4" />
            {busy ? "Filing…" : `File ${displayType === "advisor_tip" ? "advisor tip" : "narrative fact"}`}
          </button>
        </div>
      </form>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Legacy placeholder (route left in place by App.js).
// ─────────────────────────────────────────────────────────────
export function AtlasSearchPlaceholderPage() {
  const nav = useNavigate();
  return (
    <div className="p-6 md:p-10 max-w-3xl" data-testid="atlas-search-placeholder">
      <h1 className="text-tb-navy text-3xl md:text-4xl font-semibold"
          style={{ fontFamily: '"Playfair Display", serif' }}>
        Atlas Search
      </h1>
      <p className="mt-1 font-hand text-tb-navy/60 text-sm max-w-xl">
        The real semantic search lives at <b>/atlas/search</b>. Use the Knowledge Builder to keep contributing facts.
      </p>
      <button type="button"
        onClick={() => nav("/atlas/knowledge")}
        data-testid="atlas-search-placeholder-goto-kb"
        className="mt-4 inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm">
        <Sparkles className="w-4 h-4" /> Open Knowledge Builder
      </button>
    </div>
  );
}
